/**
 * Tool cards displays the filtered list of tools based on the selected
 * category.
 */

import {StyleSheet} from "aphrodite";
import React, {useCallback} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {mediaQueries} from "@khan/shared-styles";

import AIToolCard from "./ai-tool-card.tsx";
import {
    toCEDARToolCategory,
    type Tool,
    type ToolCategories,
} from "./ai-tools-list.ts";
import type {Platform} from "./utils.ts";

export enum KhanmigoToolsDashboard {
    Teacher = "TEACHER",
    Parent = "PARENT",
    Admin = "ADMIN",
}

type Props = {
    filteredTools: Array<Tool>;
    category: ToolCategories;
    platform: Platform;
    updateFullToolList?: (configName: string, isFavorited: boolean) => void;
    khanmigoToolsDashboard: KhanmigoToolsDashboard;
};

const AIToolCards = ({
    filteredTools,
    category,
    platform,
    updateFullToolList,
    khanmigoToolsDashboard,
}: Props): React.ReactElement => {
    const handleClick = useCallback(
        (tool) => {
            publishAnalyticsEventForMinorConversion({
                eventType: "AiTeacherToolsToolSelected",
                eventSchemaVersion: 4,
                aiGuideExperienceReportingLabelId: tool.reportingLabelId,
                toolCategory: toCEDARToolCategory(category),
            });
        },
        [category],
    );

    return (
        <View style={styles.container}>
            {filteredTools.map((tool) => (
                <AIToolCard
                    key={tool.urlSlug}
                    tool={tool}
                    style={styles.card}
                    onClick={handleClick}
                    platform={platform}
                    updateFullToolList={updateFullToolList}
                    showFavoriteButton={
                        khanmigoToolsDashboard ===
                        KhanmigoToolsDashboard.Teacher
                    }
                />
            ))}
        </View>
    );
};

export default AIToolCards;

export const styles = StyleSheet.create({
    container: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gridColumnGap: spacing.xLarge_32,
        gridRowGap: spacing.xLarge_32,
        marginBottom: spacing.xxLarge_48,
        [mediaQueries.mdOrSmaller]: {
            gridTemplateColumns: "1fr 1fr",
        },
        [mediaQueries.smOrSmaller]: {
            gridTemplateColumns: "1fr",
            gridRowGap: spacing.medium_16,
        },
    },
    card: {
        border: `1px solid ${color.offBlack16}`,
        borderRadius: spacing.xSmall_8,
    },
});
